import React, { Fragment, useMemo, useState } from 'react';
import clsx from 'clsx';
import BottomDrawer from '../../../../../../common/_custom/BottomDrawer/BottomDrawer';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { BankTransaction } from './types';
import { groupTransactionsByMonth } from './entityDrillDownGrouping';
import styles from './EntityDrillDownDrawer.module.scss';

interface EntityDrillDownDrawerProps {
    entityName: string;
    transactions: BankTransaction[];
    loading: boolean;
    onClose: () => void;
}

/** Plain en-IN formatted absolute value; '-' when not a finite number. No ₹ prefix. */
function formatTxnAmount(value: number | string): string {
    const n = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(n)) return '-';
    return Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** ₹-prefixed en-IN formatted absolute value for subtotal/grand-total cells. */
function formatAmount(value: number): string {
    return `₹${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** ₹-prefixed en-IN net amount that preserves sign (negative nets show a leading '-'). */
function formatNetAmount(value: number): string {
    const amount = Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value < 0 ? `-₹${amount}` : `₹${amount}`;
}

const ALL_TAB = 'all';

function EntityDrillDownDrawer({ entityName, transactions, loading, onClose }: EntityDrillDownDrawerProps) {
    const { groups } = useMemo(() => groupTransactionsByMonth(transactions), [transactions]);
    const [activeTab, setActiveTab] = useState<string>(ALL_TAB);

    const heading = (
        <span>
            <strong className={styles.DrawerHeading}>Transactions</strong>
            <span className={styles.DrawerHeadingName}>{` | ${entityName}`}</span>
        </span>
    );

    const activeMonthExists = activeTab === ALL_TAB || groups.some((g) => g.monthKey === activeTab);
    const effectiveTab = activeMonthExists ? activeTab : ALL_TAB;
    const visibleGroups = effectiveTab === ALL_TAB ? groups : groups.filter((g) => g.monthKey === effectiveTab);
    const filteredCredits = visibleGroups.reduce((s, g) => s + g.credits, 0);
    const filteredDebits = visibleGroups.reduce((s, g) => s + g.debits, 0);
    const filteredNet = filteredCredits - filteredDebits;

    return (
        <BottomDrawer heading={heading} showHeading allowClose onClose={onClose} height="50vh">
            <div className={styles.DrawerContent}>
                {loading ? (
                    <LoadingSpinner height="40px" color="black" style={{ marginTop: '2rem' }} />
                ) : transactions.length === 0 ? (
                    <div className={styles.EmptyState}>No transactions found.</div>
                ) : (
                    <>
                        {/* Month-filter tab row */}
                        <div className={styles.TabRow}>
                            <button
                                className={clsx(styles.Tab, { [styles.TabActive]: effectiveTab === ALL_TAB })}
                                onClick={() => setActiveTab(ALL_TAB)}
                            >
                                {`All ${transactions.length}`}
                            </button>
                            {groups.map((g) => (
                                <button
                                    key={g.monthKey}
                                    className={clsx(styles.Tab, { [styles.TabActive]: effectiveTab === g.monthKey })}
                                    onClick={() => setActiveTab(g.monthKey)}
                                >
                                    {g.tabLabel}
                                </button>
                            ))}
                        </div>

                        <table className={styles.Table}>
                            <thead>
                                <tr>
                                    <th>Txn. Date</th>
                                    <th>Credits (₹)</th>
                                    <th>Debit (₹)</th>
                                    <th>Transactions Notes</th>
                                    <th>Tagged Entity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleGroups.map((group) => (
                                    <Fragment key={group.monthKey || group.monthLabel}>
                                        {group.transactions.map((txn) => (
                                            <tr key={txn.id}>
                                                <td>{txn.date}</td>
                                                <td>{txn.transactionType !== 'debit' ? formatTxnAmount(txn.amount) : '-'}</td>
                                                <td>{txn.transactionType === 'debit' ? formatTxnAmount(txn.amount) : '-'}</td>
                                                <td>{txn.transactionNote}</td>
                                                <td>
                                                    {txn.taggedEntities?.length > 0
                                                        ? txn.taggedEntities.map((name) => (
                                                              <span key={name} className={styles.EntityChip}>
                                                                  {name}
                                                              </span>
                                                          ))
                                                        : null}
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.SubtotalRow}>
                                            <td>{`Total ${group.monthLabel}`}</td>
                                            <td>{formatAmount(group.credits)}</td>
                                            <td>{formatAmount(group.debits)}</td>
                                            <td>{`Net Amount: ${formatNetAmount(group.credits - group.debits)}`}</td>
                                            <td></td>
                                        </tr>
                                    </Fragment>
                                ))}
                                <tr className={clsx(styles.SubtotalRow, styles.GrandTotalRow)}>
                                    <td>Grand Total</td>
                                    <td>{formatAmount(filteredCredits)}</td>
                                    <td>{formatAmount(filteredDebits)}</td>
                                    <td>{`Net Amount: ${formatNetAmount(filteredNet)}`}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </BottomDrawer>
    );
}

export default EntityDrillDownDrawer;
